import type { DoneMeta } from "./persist";
import { DONE_STORAGE_KEY } from "./persist";

type Listener = () => void;

function load(): Record<string, DoneMeta> {
  try {
    const raw = localStorage.getItem(DONE_STORAGE_KEY);
    if (!raw) return {};
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== "object") return {};
    
    // Migrate old format (Record<string, true>) to new format
    const migrated: Record<string, DoneMeta> = {};
    for (const [uid, value] of Object.entries(obj)) {
      if (value === true) {
        // Old format
        migrated[uid] = { markedAt: Date.now(), eventUid: uid };
      } else if (value && typeof value === "object" && "markedAt" in value) {
        // New format
        migrated[uid] = value as DoneMeta;
      }
    }
    return migrated;
  } catch {
    return {};
  }
}

function save(obj: Record<string, DoneMeta>) {
  localStorage.setItem(DONE_STORAGE_KEY, JSON.stringify(obj));
}

class DoneStore {
  private map: Record<string, DoneMeta>;
  private listeners = new Set<Listener>();

  constructor() {
    this.map = load();
    save(this.map);
  }

  subscribe(fn: Listener) {
    this.listeners.add(fn);
    fn(); // Immediately call with current state
    return () => this.listeners.delete(fn);
  }

  private emit() {
    this.listeners.forEach((l) => l());
  }

  isDone(uid: string): boolean {
    return !!this.map[uid];
  }

  getMeta(uid: string): DoneMeta | null {
    return this.map[uid] || null;
  }

  markDone(uid: string) {
    if (!uid) return;
    this.map[uid] = { markedAt: Date.now(), eventUid: uid };
    save(this.map);
    this.emit();
  }

  unDone(uid: string) {
    if (!uid) return;
    if (this.map[uid]) {
      delete this.map[uid];
      save(this.map);
      this.emit();
    }
  }

  clear() {
    this.map = {};
    save(this.map);
    this.emit();
  }

  getAll(): string[] {
    return Object.keys(this.map);
  }

  getAllMeta(): Record<string, DoneMeta> {
    return { ...this.map };
  }
}

export const doneStore = new DoneStore();
